import {
  handleResp,
  getDefaultHeaders,
  extendFetchOptions,
  validateFields,
} from "./utils";
import { assignObject } from "utils";
import type { StandardObject } from "utils";
import type {
  WrappedCashifyFunction,
  CommonResponse,
} from "./utils";
import type { SupportedLocale } from "components/supported-language";
import type { HandleRespResult } from "./utils";

export interface NewMessageService {
  postMessagingSms: ReturnType<typeof postMessagingSms>;
  postMessagingTemplateSms: ReturnType<typeof postMessagingTemplateSms>;
  postMessagingBroadcastSms: ReturnType<typeof postMessagingBroadcastSms>;
  postMessagingBroadcastTemplateSms: ReturnType<typeof postMessagingBroadcastTemplateSms>;
  postMessagingEmail: ReturnType<typeof postMessagingEmail>;
  postMessagingTemplateEmail: ReturnType<typeof postMessagingTemplateEmail>;
  postMessagingBroadcastEmail: ReturnType<typeof postMessagingBroadcastEmail>;
  postMessagingBroadcastTemplateEmail: ReturnType<typeof postMessagingBroadcastTemplateEmail>;
  postMessagingPns: ReturnType<typeof postMessagingPns>;
  postMessagingTemplatePns: ReturnType<typeof postMessagingTemplatePns>;
  postMessagingBroadcastPns: ReturnType<typeof postMessagingBroadcastPns>;
  postMessagingBroadcastTemplatePns: ReturnType<typeof postMessagingBroadcastTemplatePns>;
};

// TODO: Refactor it
export interface MessageRecipient {
  userId?: string;
  // SMS
  phoneNumber?: string;
  // Email
  name?: string;
  email?: string;
  // Push Notification
  deviceId?: string;
}

export interface NewMessageRequest {
  recipient?: MessageRecipient;
  recipients?: MessageRecipient[];
  // SMS & Push Notification
  messageContent?: string;
  // Email
  subject?: string;
  content?: string;
  contentType?: string;
  // Push Notification
  messageTitle?: string;
  messageType?: string;
};

export interface NewTemplatedMessageRequest {
  recipient: MessageRecipient;
  recipients?: MessageRecipient[];
  templateName: string;
  locale: SupportedLocale;
  // SMS
  keywordArguments?: StandardObject<string>;
  // Email
  contentType?: string;
  subjectKeywordArguments?: StandardObject<string>;
  // Email & Push Notification
  contentKeywordArguments?: StandardObject<string>;
  // Push Notification
  titleKeywordArguments?: StandardObject<string>;
  messageType?: string;
}

async function handlePostNewMessage<T, R extends CommonResponse>(
  url: string,
  reqBody: T,
  options: StandardObject
): HandleRespResult<R> {
  const opts = extendFetchOptions(
    {
      method: "POST",
      body: JSON.stringify(reqBody),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    },
    options
  );
  return fetch(url, opts).then((resp) => handleResp(resp));
}

export const postMessagingSms: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipient, messageContent }) => {
    const { phoneNumber } = recipient ?? {};
    let rej = validateFields([phoneNumber, messageContent]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/sms`;
    const reqBody = {
      header: {
        recipient: {
          phoneNumber,
        },
      },
      body: {
        messageContent,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingTemplateSms: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipient, templateName, locale, keywordArguments }) => {
    const { phoneNumber } = recipient;
    let rej = validateFields([
      phoneNumber,
      templateName,
      locale,
      keywordArguments,
    ]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/sms/template`;
    const reqBody = {
      message: {
        header: {
          recipient: {
            phoneNumber,
          },
        },
        body: {
          keywordArguments,
        },
      },
      templateName,
      locale,
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastSms: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipients, messageContent }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([
      messageContent,
      ...recipients.map((r) => r.phoneNumber),
    ]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/sms/broadcasting`;
    const headers = recipients.map((r) => {
      let { phoneNumber } = r;
      return {
        recipient: {
          phoneNumber,
        },
      };
    });
    const reqBody = {
      headers,
      body: {
        messageContent,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastTemplateSms: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipients, templateName, locale, keywordArguments }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([
      templateName,
      locale,
      keywordArguments,
      ...recipients.map((r) => r.phoneNumber),
    ]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/sms/broadcasting/template`;
    const messages = recipients.map((r) => {
      const { phoneNumber } = r;
      return {
        header: {
          recipient: {
            phoneNumber,
          },
        },
        body: {
          keywordArguments,
        },
      };
    });
    let reqBody = {
      messages,
      templateName,
      locale,
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingEmail: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ subject, recipient, content, contentType }) => {
    const { name, email } = recipient ?? {};
    let rej = validateFields([subject, email, content]);
    if (!!rej) {
      return rej;
    }
    const newRecipient = assignObject({ email }, { name });
    const body = assignObject({ content }, { contentType });
    const url = `${urlPrefix}/api/v1/messaging/email`;
    const reqBody = {
      header: {
        subject,
        toRecipients: [newRecipient],
      },
      body,
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingTemplateEmail: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({
    templateName,
    locale,
    subjectKeywordArguments,
    recipient,
    contentKeywordArguments,
    contentType,
  }) => {
    let { name, email } = recipient;
    let rej = validateFields([
      templateName,
      locale,
      subjectKeywordArguments,
      email,
      contentKeywordArguments,
    ]);
    if (!!rej) {
      return rej;
    }

    const newRecipient = assignObject({ email }, { name });
    const url = `${urlPrefix}/api/v1/messaging/email/template`;
    const body = assignObject({ contentKeywordArguments }, { contentType });
    const reqBody = {
      templateName,
      locale,
      templateEmail: {
        header: {
          subjectKeywordArguments,
          toRecipients: [newRecipient],
        },
        body,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastEmail: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ subject, recipients, content, contentType }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([
      subject,
      content,
      ...recipients.map((r) => r.email),
    ]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/email/broadcasting`;
    const headers = recipients.map((r) => {
      const { email, name } = r;
      const recipient = assignObject({ email }, { name });
      return {
        subject,
        toRecipients: [recipient],
      };
    });
    const body = assignObject({ content }, { contentType });
    const reqBody = {
      headers,
      body,
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastTemplateEmail: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({
    templateName,
    locale,
    recipients,
    subjectKeywordArguments,
    contentKeywordArguments,
    contentType,
  }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([
      templateName,
      locale,
      subjectKeywordArguments,
      contentKeywordArguments,
      ...recipients.map((r) => r.email),
    ]);
    if (!!rej) {
      return rej;
    }
    const body = assignObject({ contentKeywordArguments }, { contentType });
    const templateEmails = recipients.map((r) => {
      const { email, name } = r;
      const recipient = assignObject({ email }, { name });
      return {
        header: {
          subjectKeywordArguments,
          toRecipients: [recipient],
        },
        body,
      };
    });
    const url = `${urlPrefix}/api/v1/messaging/email/broadcasting/template`;
    let reqBody = {
      templateName,
      locale,
      templateEmails,
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingPns: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipient, messageTitle, messageContent, messageType }) => {
    const { userId, deviceId } = recipient ?? {};
    let rej = validateFields([deviceId, messageTitle, messageContent]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/pns`;
    const reqBody = {
      header: {
        recipient: assignObject({ deviceId }, { userId }),
      },
      body: {
        messageTitle,
        messageContent,
      },
      additionalDetails: {
        messageType,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingTemplatePns: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ templateName, locale, recipient, titleKeywordArguments, contentKeywordArguments, messageType }) => {
    const { userId, deviceId } = recipient;
    let rej = validateFields([templateName, locale, deviceId, titleKeywordArguments, contentKeywordArguments]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/pns/template`;
    const reqBody = {
      message: {
        header: {
          recipient: assignObject({ deviceId }, { userId }),
        },
        body: {
          titleKeywordArguments,
          contentKeywordArguments,
        },
      },
      templateName,
      locale,
      additionalDetails: {
        messageType,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastPns: WrappedCashifyFunction<NewMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ recipients, messageTitle, messageContent, messageType }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([messageTitle, messageContent, ...recipients.map((r) => r.deviceId)]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/pns/broadcasting`;
    const headers = recipients.map((r) => {
      const { deviceId, userId } = r;
      const recipient = assignObject({ deviceId }, { userId });
      return {
        recipient,
      };
    });
    const reqBody = {
      headers: headers,
      body: {
        messageTitle,
        messageContent,
      },
      additionalDetails: {
        messageType,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};

export const postMessagingBroadcastTemplatePns: WrappedCashifyFunction<NewTemplatedMessageRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ templateName, locale, recipients, titleKeywordArguments, contentKeywordArguments, messageType }) => {
    if (!recipients) {
      return Promise.reject("Invalid recipients.");
    }
    let rej = validateFields([templateName, locale, titleKeywordArguments, contentKeywordArguments, ...recipients.map((r) => r.deviceId)]);
    if (!!rej) {
      return rej;
    }
    const url = `${urlPrefix}/api/v1/messaging/pns/broadcasting/template`;
    const messages = recipients.map((r) => {
      const { deviceId, userId } = r;
      const recipient = assignObject({ deviceId }, { userId });
      return {
        header: {
          recipient,
        },
        body: {
          titleKeywordArguments,
          contentKeywordArguments,
        },
      };
    });
    const reqBody = {
      messages,
      templateName,
      locale,
      additionalDetails: {
        messageType,
      },
    };
    return handlePostNewMessage(url, reqBody, options);
  };
};